import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { PieChartInternal } from './components/PieChartInternal.js';
import {
  CHART_VARIANT,
  PIE_CHART_ARIA_LABEL,
  PIE_CHART_DISPLAY_NAME,
} from './constants.js';
import { usePieChartConfig } from './hooks/usePieChartConfig.js';
import {
  ChartProviders,
  type ChartProvidersProps,
} from './providers/ChartProviders.js';
import { Grouping as PieChartGrouping } from './slots/PieChartGrouping.js';
import { Labels as PieChartLabels } from './slots/PieChartLabels.js';
import { Legend as PieChartLegend } from './slots/PieChartLegend.js';
import { Tooltip as PieChartTooltipSlot } from './slots/PieChartTooltip.js';
import type { PieChartRef } from './types/pie-chart-ref.js';
import type {
  PieChartProps,
  PieDonutConfig,
} from './types/pie-chart.config.js';
import { buildAndDownloadCsv } from './utils/build-and-download-csv.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { getToolbarConfigWithoutIntents } from '../core/components/toolbar/utils/get-toolbar-config-without-intents.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { ChartType } from '../core/types/chart-types.js';
import { Toolbar } from './shared/slots/Toolbar.js';
import { PieChartDownloadCSVSlot } from './slots/PieChartDownloadCSV.js';

const COMPONENT_NAME = 'PieChart';
const sanitizeToolbarIntents = (config: PieDonutConfig): PieDonutConfig => {
  const toolbar = config.toolbar
    ? getToolbarConfigWithoutIntents(config.toolbar)
    : undefined;

  return {
    ...config,
    toolbar,
  };
};

const PieChartRoot = forwardRef<PieChartRef, PropsWithChildren<PieChartProps>>(
  (props, forwardedRef) => {
    const {
      className,
      style,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-testid': dataTestId = 'pie-chart-root',
    } = props;

    const chartConfig = usePieChartConfig(props);

    const { emptyState, errorState } = getChartStatesTemplates(props.children);

    const isDataAvailable = !isEmpty(props.data);

    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useMergeRefs<PieChartRef | HTMLDivElement>([
      forwardedRef,
      containerRef,
    ]);

    useImperativeHandle(
      forwardedRef,
      (): PieChartRef => ({
        element: containerRef.current,
        getConfig: () =>
          JSON.stringify({
            type: ChartType.Pie,
            ...sanitizeToolbarIntents(chartConfig),
          }),
        downloadData: () => {
          buildAndDownloadCsv(props.data);
        },
      }),
    );

    const providersProps: ChartProvidersProps = {
      chartData: props.data,
      chartConfig,
    };

    return (
      <ChartProviders {...providersProps}>
        <PieChartInternal
          {...props}
          ref={rootRef}
          height={chartConfig.height}
          variant={CHART_VARIANT.Pie}
          isEmpty={!isDataAvailable}
          emptyState={emptyState}
          errorState={errorState}
          className={clsx(defaultChartSize, className)}
          style={style}
          data-dt-component={COMPONENT_NAME}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          aria-label={PIE_CHART_ARIA_LABEL}
          role="img"
        />
      </ChartProviders>
    );
  },
);

(PieChartRoot as typeof PieChartRoot & { displayName: string }).displayName =
  PIE_CHART_DISPLAY_NAME;

/**
 * The `PieChart` visualizes data proportions in a circular chart.
 * It divides a circle into slices, with each slice representing a specific
 * category or value.
 * @public
 */
export const PieChart = Object.assign(PieChartRoot, {
  Legend: PieChartLegend,
  Grouping: PieChartGrouping,
  Labels: PieChartLabels,
  Tooltip: PieChartTooltipSlot,
  Intent,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  ColorRule,
  Toolbar,
  DownloadCSV: PieChartDownloadCSVSlot,
});
